import * as LoggerProxy from './logger-proxy';
const REGEX_TIMEOUT_MS = 250;
const REGEX_TIMEOUT_ERROR_MESSAGE = 'Regular expression execution timed out';
let warnedAboutBrowserFallback = false;
let engine;
export function parseRegexLiteral(value) {
    const literal = value.toString();
    const match = /^\/(.*?)\/([gimusy]*)$/.exec(literal);
    if (!match)
        return { source: literal, flags: '' };
    return { source: match[1] ?? '', flags: match[2] ?? '' };
}
function globalFlag(flags) {
    return flags?.includes('g') ? flags : `${flags ?? ''}g`;
}
function isNodeRuntime() {
    const processValue = Reflect.get(globalThis, 'process');
    if (typeof processValue !== 'object' || processValue === null)
        return false;
    const versions = Reflect.get(processValue, 'versions');
    if (typeof versions !== 'object' || versions === null)
        return false;
    return typeof Reflect.get(versions, 'node') === 'string';
}
function loadNodeVm() {
    if (!isNodeRuntime())
        return undefined;
    const processValue = Reflect.get(globalThis, 'process');
    if (typeof processValue === 'object' && processValue !== null) {
        const getBuiltinModule = Reflect.get(processValue, 'getBuiltinModule');
        if (typeof getBuiltinModule === 'function') {
            const vmModule = getBuiltinModule('node:vm');
            if (vmModule)
                return vmModule;
        }
    }
}
function isTimeout(error) {
    return (typeof error === 'object' &&
        error !== null &&
        Reflect.get(error, 'code') === 'ERR_SCRIPT_EXECUTION_TIMEOUT');
}
function nativeEngine() {
    if (!warnedAboutBrowserFallback) {
        warnedAboutBrowserFallback = true;
        LoggerProxy.warn('Using native regular expression engine without timeout protection');
    }
    /* eslint-disable n8n-local-rules/no-dynamic-regexp -- isomorphic native fallback; backend overrides via setSafeRegexEngine */
    return {
        exec: (pattern, input, flags) => new RegExp(pattern, flags).exec(input),
        test: (pattern, input, flags) => new RegExp(pattern, flags).test(input),
        replace: (pattern, input, flags, replacement) => input.replace(new RegExp(pattern, flags), replacement),
        matchAll: (pattern, input, flags) => Array.from(input.matchAll(new RegExp(pattern, globalFlag(flags)))),
        split: (pattern, input, flags) => input.split(new RegExp(pattern, flags)),
    };
    /* eslint-enable n8n-local-rules/no-dynamic-regexp */
}
function nodeVmEngine(vm) {
    const context = {
        flags: '',
        input: '',
        pattern: '',
        replacement: '',
        result: undefined,
    };
    const vmContext = vm.createContext(context);
    const scripts = {
        exec: new vm.Script('result = new RegExp(pattern, flags).exec(input)'),
        test: new vm.Script('result = new RegExp(pattern, flags).test(input)'),
        replace: new vm.Script('result = input.replace(new RegExp(pattern, flags), replacement)'),
        matchAll: new vm.Script('result = Array.from(input.matchAll(new RegExp(pattern, flags)))'),
        split: new vm.Script('result = input.split(new RegExp(pattern, flags))'),
    };
    function run(script) {
        try {
            script.runInContext(vmContext, { timeout: REGEX_TIMEOUT_MS });
            return context.result;
        }
        catch (error) {
            if (isTimeout(error))
                throw new Error(REGEX_TIMEOUT_ERROR_MESSAGE);
            throw error;
        }
    }
    return {
        exec(pattern, input, flags) {
            context.pattern = pattern;
            context.flags = flags ?? '';
            context.input = input;
            context.result = undefined;
            return run(scripts.exec);
        },
        test(pattern, input, flags) {
            context.pattern = pattern;
            context.flags = flags ?? '';
            context.input = input;
            context.result = undefined;
            return run(scripts.test);
        },
        replace(pattern, input, flags, replacement) {
            context.pattern = pattern;
            context.flags = flags ?? '';
            context.input = input;
            context.replacement = replacement;
            context.result = undefined;
            return run(scripts.replace);
        },
        matchAll(pattern, input, flags) {
            context.pattern = pattern;
            context.flags = globalFlag(flags);
            context.input = input;
            context.result = undefined;
            return run(scripts.matchAll);
        },
        split(pattern, input, flags) {
            context.pattern = pattern;
            context.flags = flags ?? '';
            context.input = input;
            context.result = undefined;
            return run(scripts.split);
        },
    };
}
function createDefaultEngine() {
    const vm = loadNodeVm();
    return vm ? nodeVmEngine(vm) : nativeEngine();
}
export function setSafeRegexEngine(regexEngine) {
    engine = regexEngine;
}
export function resetSafeRegexEngine() {
    engine = createDefaultEngine();
}
engine = createDefaultEngine();
export const safeRegex = {
    exec: (pattern, input, flags) => engine.exec(pattern, input, flags),
    test: (pattern, input, flags) => engine.test(pattern, input, flags),
    replace: (pattern, input, flags, replacement) => engine.replace(pattern, input, flags, replacement),
    matchAll: (pattern, input, flags) => engine.matchAll(pattern, input, flags),
    split: (pattern, input, flags) => engine.split(pattern, input, flags),
};
//# sourceMappingURL=safe-regex.js.map